import React, { useState, useMemo } from 'react';
import { Layout, Row, Col, Card, Alert } from 'antd';
import OWPdfViewer from '../ow_pdf_viewer/OWPdfViewer';
import { InvoiceFormBase, createSupplierInvoiceConfig } from '../InvoiceFormBase';
import InvoiceUploadandParse from '../invoice_uploader/InvoiceUploadandParse';
import { withRouter } from 'react-router-dom';

import { connect } from 'react-redux';
import { createInvoiceBasedOnWorkOrder } from '../../thunks/invoices_thunks';
import {
	createInvoiceSubContractor,
	updateInvoiceSubContractor,
	invoicesRestCrudThunksForSupplier,
} from '../../thunks/invoices_thunks';
import { withUserType } from '../withUserType';
import { adaptParsedInvoiceToFormData } from '../../utils/InvoiceDataAdapter';
import { getInvoiceEntityCommonValues } from '../../utils/InvoiceUtils';
import { isSubContractor, nullSafeGet } from '../../utils/DataAccessUtils';
import { getContractorWO } from '../../utils/WorkOrderUtils';
import { isInternalTech } from '../../utils/AuthUtils';
import { TIME_IN } from '../supplier_invoice_form/LaborTimeSelect';

const { Content } = Layout;

const NewSupplierUploadInvoicePage = ({
	workOrder,
	currentUser,
	history,
	createInvoice,
	updateInvoice,
	createContractorInvoice,
	updateContractorInvoice,
	creating,
	updating,
	createErrors,
	updateErrors,
}) => {
	const [formData, setFormData] = useState(null);
	const [pdfUrl, setPdfUrl] = useState(null);
	const [formKey, setFormKey] = useState(0);
	const [parsedDiscount, setParsedDiscount] = useState(null);

	const handleDeletePdf = () => {
		setPdfUrl(null);
		setFormData(null);
		setParsedDiscount(null);
		setFormKey((prev) => prev + 1);
	};

	const handleUploadSuccess = (data) => {
		if (data.parsedData && workOrder) {
			// Adapt parsed data to form data structure
			const adaptedFormData = adaptParsedInvoiceToFormData(data.parsedData, workOrder);
			setFormData(adaptedFormData);
			setParsedDiscount(data.parsedData.discount);
		}
		setPdfUrl(data.fileUrl);
		setFormKey((prev) => prev + 1);
	};

	// Handle form submission
	const handleSubmitMemo = useMemo(
		() => async (values, formInstance, scope) => {
			const contractorWO = getContractorWO(workOrder, false);
			const isUpdate = values.id && formData && formData.id;
			const submitAction = isUpdate ? updateInvoice : createInvoice;
			const submitContractorAction = isUpdate ? updateContractorInvoice : createContractorInvoice;

			const supplierFacilityId = isInternalTech(currentUser)
				? formData.supplierFacilityId
				: values.supplierFacilityId || nullSafeGet('facility.id', currentUser);

			const invoice = {
				...getInvoiceEntityCommonValues(values, formData, scope, currentUser, TIME_IN.HOURS),
				status: isInternalTech(currentUser) ? 'pending' : 'draft',
				invoicePDFLink:
					values.invoiceTotalAfterTax !== (formData && formData.invoiceTotalAfterTax)
						? undefined
						: formData && formData.invoicePDFLink,
				supplierFacilityId,
				disputeNotes: formData && formData.disputeNotes,
			};

			if (contractorWO && isSubContractor(currentUser, workOrder)) {
				return submitContractorAction(invoice);
			} else {
				return submitAction(invoice);
			}
		},
		[
			workOrder,
			formData,
			currentUser,
			createInvoice,
			updateInvoice,
			createContractorInvoice,
			updateContractorInvoice,
		]
	);

	// Create form config
	const formConfig = useMemo(() => {
		if (!formData || !workOrder || !currentUser || !history) {
			return null;
		}

		return createSupplierInvoiceConfig(
			formData,
			workOrder,
			currentUser,
			history,
			handleSubmitMemo,
			{
				creating,
				updating,
				createErrors,
				updateErrors,
				onSuccess: (record) => {
					// Handle success if needed
				},
				onError: (error) => {
					console.error('Error creating invoice', error);
				},
			}
		);
	}, [
		formData,
		workOrder,
		currentUser,
		history,
		creating,
		updating,
		createErrors,
		updateErrors,
		handleSubmitMemo,
	]);

	return (
		<Content>
			<Row>
				<Col span={24}>
					<Row style={{ minHeight: '100vh' }} align="stretch">
						<Col
							span={12}
							style={{
								height: '100%',
								padding: 0,
								overflowY: 'auto',
								maxHeight: '100vh',
							}}
						>
							<Card>
								<div
									style={{
										paddingRight: '8px',
										padding: 12,
									}}
								>
									<h5 style={{ marginBottom: 24 }}>Create Invoice</h5>
									{formData && (
										<Alert
											message="Invoice fields have been auto-filled from the uploaded file. Please review the details and make any corrections if needed."
											type="success"
											showIcon
											style={{ marginBottom: 16 }}
										/>
									)}
									{/* Note: Parsed total comparison alert removed temporarily 
										as InvoiceFormBase doesn't expose totalAfterTax. 
										This can be re-added when we add a callback to track totals. */}
									{parsedDiscount ? (
										<Alert
											message="We detected a discount on the invoice. We have applied discount on each line item. Please review the invoice details."
											type="info"
											showIcon
											style={{ marginBottom: 16 }}
										/>
									) : null}
									{formConfig && <InvoiceFormBase key={formKey} config={formConfig} />}
								</div>
							</Card>
						</Col>
						<Col span={12} style={{ height: '100%' }}>
							<div style={{ height: '100%', maxHeight: '100vh', overflowY: 'scroll' }}>
								<Card className="h-full">
									{pdfUrl ? (
										<OWPdfViewer
											file={pdfUrl}
											fileName="Invoice.pdf"
											showDelete={true}
											onDelete={handleDeletePdf}
										/>
									) : (
										<InvoiceUploadandParse
											handleUploadSuccess={handleUploadSuccess}
											uploaderType="dragger"
										/>
									)}
								</Card>
							</div>
						</Col>
					</Row>
				</Col>
			</Row>
		</Content>
	);
};

const mapStateToProps = (state, ownProps) => ({
	history: ownProps.history,
	currentUser: state.session.currentUser,
	workOrder: state.work_orders.detail,
	creating: state.invoices.creating,
	updating: state.invoices.updating,
	createErrors: state.invoices.createErrors,
	updateErrors: state.invoices.updateErrors,
});

const mapDispatchToProps = (dispatch) => ({
	createInvoiceWithWorkOrderId: (workOrderId, entity) =>
		dispatch(createInvoiceBasedOnWorkOrder(workOrderId)(entity)),
	createInvoice: (invoice) => dispatch(invoicesRestCrudThunksForSupplier.create(invoice)),
	updateInvoice: (invoice) => dispatch(invoicesRestCrudThunksForSupplier.update(invoice)),
	createContractorInvoice: (invoice) => dispatch(createInvoiceSubContractor(invoice)),
	updateContractorInvoice: (invoice) => dispatch(updateInvoiceSubContractor(invoice)),
});

export default withRouter(
	withUserType(connect(mapStateToProps, mapDispatchToProps)(NewSupplierUploadInvoicePage))
);
